import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Category } from '../../types/board';

export type CatModalMode = 'create' | 'rename' | 'delete';

function Panel({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-[95] bg-[oklch(24%_0.02_50_/_0.35)] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-[340px] max-w-[92vw] bg-card rounded-[10px] shadow-[0_12px_40px_oklch(24%_0.02_50_/_0.25)] px-6.5 pt-6.5 pb-5 z-[96]"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export default function CategoryModal({
  mode,
  initialName,
  categories,
  excludeId,
  onAdd,
  onRename,
  onDelete,
  onClose,
}: {
  mode: CatModalMode;
  initialName: string;
  categories: Category[];
  excludeId?: string;
  onAdd: (name: string) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (mode === 'delete') {
    return (
      <Panel onClose={onClose}>
        <div className="font-serif italic text-[19px] mb-2.5">Delete category?</div>
        <div className="text-[13px] text-muted mb-5">
          &quot;{initialName}&quot; will be removed from all items. This can't be undone.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-[13px]">
            Cancel
          </button>
          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            className="rounded-lg bg-terra text-cream px-4 py-2 text-[13px]"
          >
            Delete
          </button>
        </div>
      </Panel>
    );
  }

  const save = () => {
    const n = name.trim();
    if (!n) {
      setError('Name required');
      return;
    }
    const dup = categories.some(
      (c) => c.id !== excludeId && c.name.trim().toLowerCase() === n.toLowerCase(),
    );
    if (dup) {
      setError('That name is already used');
      return;
    }
    if (mode === 'create') onAdd(n);
    else onRename(n);
    onClose();
  };

  return (
    <Panel onClose={onClose}>
      <div className="font-serif italic text-[19px] mb-3.5">
        {mode === 'create' ? 'New category' : 'Rename category'}
      </div>
      <input
        autoFocus
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        className="w-full border border-line bg-cream px-2.5 py-2 text-sm outline-none focus:border-terra"
      />
      {error && <div className="text-xs text-[oklch(50%_0.15_25)] mt-1.5">{error}</div>}
      <div className="flex justify-end gap-2 mt-4.5">
        <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-[13px]">
          Cancel
        </button>
        <button onClick={save} className="rounded-lg bg-terra text-cream px-4 py-2 text-[13px]">
          {mode === 'create' ? 'Add' : 'Save'}
        </button>
      </div>
    </Panel>
  );
}
